import {
  CopyOutlined,
  DesktopOutlined,
  EditOutlined,
  MobileOutlined,
  MoreOutlined,
  PlusOutlined,
  QuestionCircleOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import {
  Alert,
  Button,
  Dropdown,
  Input,
  InputNumber,
  message,
  Select,
  Slider,
  Spin,
  Switch,
  Tabs,
  Upload,
} from "antd";
import {
  PointerEvent as ReactPointerEvent,
  ReactNode,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  FaBehance,
  FaDiscord,
  FaFacebookF,
  FaGithub,
  FaInstagram,
  FaLinkedinIn,
  FaSlack,
  FaTiktok,
  FaTwitch,
  FaWhatsapp,
  FaYoutube,
} from "react-icons/fa";
import {FaXTwitter} from "react-icons/fa6";
import {FcGoogle} from "react-icons/fc";
import {FiGlobe} from "react-icons/fi";
import {Reorder} from "framer-motion";
import {DashboardWrapper} from "../../layouts/dashboard-wrapper";
import {useGetProfileContext} from "../../context/profile/profile-context";
import {REBRAND_BASE_URL} from "../../services/config";
import {
  createDefaultPublicProfileDocument,
  createPublicProfileBlock,
  PublicProfileBlock,
  PublicProfileBlockLibraryItem,
  PublicProfileDocument,
  PublicProfileSurfaceStyle,
  PublicProfileThemeSettings,
  PUBLIC_PROFILE_THEME_FONTS,
  PUBLIC_PROFILE_THEMES,
  resolvePublicProfileThemeSettings,
  reorderPublicProfileBlocks,
  validatePublicProfileDocument,
} from "../domain";
import {
  usePublicProfileContentBlocks,
  usePublicProfileTheme,
  useSavePublicProfileContentBlocks,
  useSavePublicProfileTheme,
  useUploadPublicProfileImage,
} from "../hooks";
import {
  mergeContentBlocks,
  mergePublicProfileTheme,
  PublicProfilePageState,
} from "../api";
import {AddPublicProfileBlockModal} from "./add-block-modal";
import {PublicProfileBlockEditor} from "./block-editor";
import {LocalPublicProfilePreview} from "./local-preview";
import {UnsplashCoverImage, UnsplashPicker} from "./unsplash-picker";
import {
  BuilderHeader,
  BackgroundStyleCard,
  BackgroundStyleGrid,
  BlockStyleCard,
  BlockStyleGrid,
  ButtonStyleCard,
  ButtonStyleGrid,
  BuilderCommandBar,
  BuilderPage,
  BuilderShell,
  ColourOverrideGrid,
  ContentActions,
  ContentSectionCard,
  EditorPanel,
  FieldGroup,
  HeaderLayoutGrid,
  HeaderLayoutOption,
  HeaderProfileFields,
  PreviewPanel,
  PreviewViewport,
  SettingRow,
  SeoSettingsCard,
  ThemeCard,
  ThemeAccordion,
  ThemeGrid,
  ValidationSummary,
  VisualQaShell,
} from "./public-profile-page.style";

const SOCIAL_PLATFORM_OPTIONS = [
  ["instagram", "Instagram", FaInstagram],
  ["linkedin", "LinkedIn", FaLinkedinIn],
  ["twitter", "X", FaXTwitter],
  ["behance", "Behance", FaBehance],
  ["facebook", "Facebook", FaFacebookF],
  ["youtube", "YouTube", FaYoutube],
  ["tiktok", "TikTok", FaTiktok],
  ["github", "GitHub", FaGithub],
  ["website", "Website", FiGlobe],
  ["discord", "Discord", FaDiscord],
  ["twitch", "Twitch", FaTwitch],
].map(([value, name, Icon]) => ({
  value: value as string,
  label: (
    <span
      className="social-platform-icon"
      aria-label={name as string}
      title={name as string}
    >
      <Icon aria-hidden="true" />
    </span>
  ),
}));

const PRESET_COLOUR_FALLBACKS: Record<
  PublicProfileDocument["theme"]["preset"],
  Record<keyof PublicProfileDocument["theme"]["overrides"], string>
> = {
  mono: {
    pageBackground: "#f5f5f5",
    surface: "#ffffff",
    surfaceMuted: "#f5f5f5",
    text: "#1c1726",
    textMuted: "#4a4650",
    accent: "#222222",
    accentText: "#ffffff",
  },
  ledger: {
    pageBackground: "#eae6df",
    surface: "#f9f8f6",
    surfaceMuted: "#ffffff",
    text: "#333333",
    textMuted: "#5c5c5c",
    accent: "#333333",
    accentText: "#ffffff",
  },
  frost: {
    pageBackground: "#dee8fb",
    surface: "#f1f5f9",
    surfaceMuted: "#ffffff",
    text: "#222222",
    textMuted: "#4e4e4e",
    accent: "#ffffff",
    accentText: "#222222",
  },
  lush: {
    pageBackground: "#e8d8ce",
    surface: "#fdfbf7",
    surfaceMuted: "#e8d8ce",
    text: "#3d2b1f",
    textMuted: "#64564c",
    accent: "#e8d8ce",
    accentText: "#3d2b1f",
  },
  midnight: {
    pageBackground: "#111111",
    surface: "#191919",
    surfaceMuted: "#191919",
    text: "#f5f5f5",
    textMuted: "#c4c4c4",
    accent: "#f5f5f5",
    accentText: "#111111",
  },
  velvet: {
    pageBackground: "#2a1b38",
    surface: "#3b284f",
    surfaceMuted: "#2a1b38",
    text: "#e9d4ff",
    textMuted: "#d4bfe8",
    accent: "#b892ff",
    accentText: "#2a1b38",
  },
  mint: {
    pageBackground: "#a7f3d0",
    surface: "#f6fffb",
    surfaceMuted: "#ebfff6",
    text: "#222222",
    textMuted: "#4e4e4e",
    accent: "#10b981",
    accentText: "#052e25",
  },
  terminal: {
    pageBackground: "#0d1117",
    surface: "#0d1117",
    surfaceMuted: "#0d1117",
    text: "#3fb950",
    textMuted: "#329440",
    accent: "#3fb950",
    accentText: "#0d1117",
  },
  retro: {
    pageBackground: "#9d4edd",
    surface: "#ffffff",
    surfaceMuted: "#f15bb5",
    text: "#222222",
    textMuted: "#4e4e4e",
    accent: "#ffffff",
    accentText: "#222222",
  },
  brutal: {
    pageBackground: "#ffffff",
    surface: "#ffffff",
    surfaceMuted: "#ffffff",
    text: "#222222",
    textMuted: "#4e4e4e",
    accent: "#ffd700",
    accentText: "#222222",
  },
};

const VISIBLE_THEME_PRESETS = ["mono", "ledger", "frost", "lush", "midnight"];

const THEME_PRESET_PREVIEWS: Record<string, string> = {
  mono: "linear-gradient(135deg, #1f1f1f 5%, #3d3d3d 55%, #f1f1f1 100%)",
  ledger: "linear-gradient(180deg, #3c3c3c 0%, #525252 55%, #d6d6d6 100%)",
  frost: "linear-gradient(135deg, #d7e3f9 0%, #f7f9fd 100%)",
  lush: "linear-gradient(135deg, #ead8ce 0%, #f8f1ed 100%)",
  midnight: "linear-gradient(135deg, #070707 0%, #171717 65%, #575757 100%)",
};

type PublicProfileBuilderTab = "content" | "design" | "seo";

function getQueryErrorMessage(error: unknown) {
  if (
    error &&
    typeof error === "object" &&
    "message" in error &&
    typeof error.message === "string"
  )
    return error.message;
  return "The request did not return usable data.";
}

type PublicProfileLocalEditingSession = {
  version: 1;
  document: PublicProfileDocument;
  baseline: string;
  activeTab: PublicProfileBuilderTab;
  mobilePreview: boolean;
  headerExpanded: boolean;
  expandedBlockId: string | null;
};

function publicProfileEditingSessionKey(username: string) {
  return `coachli:public-profile-builder:v1:${username || "current-user"}`;
}

function readPublicProfileEditingSession(
  username: string
): PublicProfileLocalEditingSession | null {
  try {
    const raw = window.localStorage.getItem(publicProfileEditingSessionKey(username));
    if (!raw) return null;
    const session = JSON.parse(raw) as PublicProfileLocalEditingSession;
    if (session.version !== 1 || session.document?.schemaVersion !== 1) return null;
    return session;
  } catch {
    return null;
  }
}

function writePublicProfileEditingSession(
  username: string,
  session: PublicProfileLocalEditingSession
) {
  try {
    window.localStorage.setItem(
      publicProfileEditingSessionKey(username),
      JSON.stringify(session)
    );
  } catch {
    // The builder remains usable when storage is unavailable or full.
  }
}

function restoreLocalPresentationSettings(
  backendDocument: PublicProfileDocument,
  localDocument: PublicProfileDocument
): PublicProfileDocument {
  const localBlocks = new Map(localDocument.blocks.map((block) => [block.id, block]));
  return {
    ...backendDocument,
    header: localDocument.header,
    seo: localDocument.seo,
    footer: localDocument.footer,
    blocks: backendDocument.blocks.map((block) => {
      const localBlock = localBlocks.get(block.id);
      if (!localBlock || localBlock.type !== block.type) return block;
      if (block.type === "offeringCollection" && localBlock.type === block.type) {
        return {
          ...block,
          visible: localBlock.visible,
          layout: localBlock.layout,
          mobileColumns: localBlock.mobileColumns,
          buttonLabel: localBlock.buttonLabel,
        };
      }
      return {...block, visible: localBlock.visible};
    }),
  };
}

function ThemeColourInput({
  label,
  value,
  fallback,
  allowPreset = false,
  onChange,
}: {
  label: string;
  value: string | null;
  fallback: string;
  allowPreset?: boolean;
  onChange: (value: string | null) => void;
}) {
  return (
    <FieldGroup>
      <label>{label}</label>
      <div className="colour-input">
        <input
          type="color"
          aria-label={`${label} colour picker`}
          value={value && /^#[0-9a-f]{6}$/i.test(value) ? value : fallback}
          onChange={(event) => onChange(event.target.value)}
        />
        <Input
          maxLength={7}
          value={value ?? ""}
          placeholder={allowPreset ? "Use preset" : fallback}
          onChange={(event) =>
            onChange(event.target.value || (allowPreset ? null : fallback))
          }
        />
      </div>
    </FieldGroup>
  );
}

export function PublicProfilePageBuilder({
  visualQaState,
  standalone = false,
  embedded = false,
}: {
  visualQaState?: PublicProfilePageState;
  standalone?: boolean;
  embedded?: boolean;
} = {}) {
  const {state: profileState} = useGetProfileContext();
  const username = profileState.profileData.username;
  const profileImageUrl =
    profileState.profileData.avatar || "/images/no-image-placeholder.png";
  const profileDisplayName =
    profileState.profileData.display_name ||
    `${profileState.profileData.firstName} ${profileState.profileData.lastName}`;
  const profileSocialLinks = useMemo(
    () =>
      [
        "instagram",
        "linkedin",
        "twitter",
        "behance",
        "facebook",
        "youtube",
        "tiktok",
        "github",
        "website",
        "discord",
        "twitch",
      ]
        .map((name) => ({
          name,
          url: profileState.profileData[name as keyof typeof profileState.profileData],
        }))
        .filter((item): item is {name: string; url: string} =>
          Boolean(typeof item.url === "string" && item.url)
        ),
    [profileState]
  );
  const contentBlocksQuery = usePublicProfileContentBlocks({enabled: !visualQaState});
  const themeQuery = usePublicProfileTheme({enabled: !visualQaState});
  const contentBlocksReady =
    Boolean(visualQaState) || contentBlocksQuery.data !== undefined;
  const themeReady = Boolean(visualQaState) || themeQuery.data !== undefined;
  const contentBlocksUnavailable =
    !visualQaState && contentBlocksQuery.isError && !contentBlocksReady;
  const themeUnavailable = !visualQaState && themeQuery.isError && !themeReady;
  const saveContentBlocks = useSavePublicProfileContentBlocks();
  const saveTheme = useSavePublicProfileTheme();
  const uploadThemeImage = useUploadPublicProfileImage();
  const [document, setDocument] = useState<PublicProfileDocument>(
    createDefaultPublicProfileDocument
  );
  const [baseline, setBaseline] = useState("");
  const [initialised, setInitialised] = useState(false);
  const [activeTab, setActiveTab] = useState<PublicProfileBuilderTab>("content");
  const [mobilePreview, setMobilePreview] = useState(false);
  const [mobileTrayExpanded, setMobileTrayExpanded] = useState(false);
  const mobileTrayDragStart = useRef<number | null>(null);
  const [headerExpanded, setHeaderExpanded] = useState(false);
  const [expandedBlockId, setExpandedBlockId] = useState<string | null>(null);
  const [addBlockVisible, setAddBlockVisible] = useState(false);
  const [unsplashPickerTarget, setUnsplashPickerTarget] = useState<
    "cover" | "seo" | null
  >(null);
  const [showValidationIssues, setShowValidationIssues] = useState(false);
  const renderPage = (content: ReactNode) =>
    embedded ? (
      <>{content}</>
    ) : standalone ? (
      <VisualQaShell data-visual-qa="public-profile-builder">{content}</VisualQaShell>
    ) : (
      <DashboardWrapper pageTitle="Public profile" wideContent>
        {content}
      </DashboardWrapper>
    );

  useEffect(() => {
    if (!contentBlocksReady || !themeReady || initialised) return;
    const pageDocument =
      visualQaState?.draft?.document ??
      visualQaState?.published?.document ??
      createDefaultPublicProfileDocument();
    const contentDocument = visualQaState
      ? pageDocument
      : mergeContentBlocks(pageDocument, contentBlocksQuery.data ?? []);
    const initialDocument =
      visualQaState || !themeQuery.data
        ? contentDocument
        : {
            ...contentDocument,
            theme: mergePublicProfileTheme(contentDocument.theme, themeQuery.data),
          };
    const localSession = visualQaState ? null : readPublicProfileEditingSession(username);
    const hasLocalChanges = Boolean(
      localSession && JSON.stringify(localSession.document) !== localSession.baseline
    );
    const restoredDocument = localSession
      ? hasLocalChanges
        ? localSession.document
        : restoreLocalPresentationSettings(initialDocument, localSession.document)
      : initialDocument;
    setDocument(restoredDocument);
    setBaseline(
      hasLocalChanges && localSession
        ? localSession.baseline
        : JSON.stringify(initialDocument)
    );
    setActiveTab(localSession?.activeTab ?? "content");
    setMobilePreview(localSession?.mobilePreview ?? false);
    setHeaderExpanded(localSession?.headerExpanded ?? false);
    setExpandedBlockId(localSession?.expandedBlockId ?? null);
    setInitialised(true);
  }, [
    contentBlocksQuery.data,
    contentBlocksReady,
    initialised,
    themeQuery.data,
    themeReady,
    username,
    visualQaState,
  ]);

  useEffect(() => {
    if (!initialised || visualQaState) return;
    writePublicProfileEditingSession(username, {
      version: 1,
      document,
      baseline,
      activeTab,
      mobilePreview,
      headerExpanded,
      expandedBlockId,
    });
  }, [
    activeTab,
    baseline,
    document,
    expandedBlockId,
    headerExpanded,
    initialised,
    mobilePreview,
    username,
    visualQaState,
  ]);

  const serializedDocument = useMemo(() => JSON.stringify(document), [document]);
  const isDirty = initialised && serializedDocument !== baseline;
  const showSaveAction = isDirty || headerExpanded || Boolean(expandedBlockId);
  const validationIssues = useMemo(
    () => validatePublicProfileDocument(document),
    [document]
  );
  const publicUrl = `${REBRAND_BASE_URL}/${username}`;
  const publicUrlLabel = `coachli.co/${username}`;
  const editableSocialLinks =
    document.header.socialLinks ?? profileSocialLinks.slice(0, 5);

  const handleMobileTrayPointerDown = (event: ReactPointerEvent<HTMLButtonElement>) => {
    mobileTrayDragStart.current = event.clientY;
    event.currentTarget.setPointerCapture(event.pointerId);
  };

  const handleMobileTrayPointerUp = (event: ReactPointerEvent<HTMLButtonElement>) => {
    const startY = mobileTrayDragStart.current;
    mobileTrayDragStart.current = null;
    if (startY === null) return;

    const distance = event.clientY - startY;
    if (Math.abs(distance) < 12) {
      setMobileTrayExpanded((current) => !current);
      return;
    }
    setMobileTrayExpanded(distance < 0);
  };

  const updateSocialLink = (index: number, key: "name" | "url", value: string) => {
    updateHeader(
      "socialLinks",
      editableSocialLinks.map((link, linkIndex) =>
        linkIndex === index ? {...link, [key]: value} : link
      )
    );
  };

  const addSocialLink = () => {
    if (editableSocialLinks.length >= 5) return;
    updateHeader("socialLinks", [...editableSocialLinks, {name: "website", url: ""}]);
  };

  useEffect(() => {
    const warnBeforeLeaving = (event: BeforeUnloadEvent) => {
      if (!isDirty) return;
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", warnBeforeLeaving);
    return () => window.removeEventListener("beforeunload", warnBeforeLeaving);
  }, [isDirty]);

  const updateHeader = <K extends keyof PublicProfileDocument["header"]>(
    key: K,
    value: PublicProfileDocument["header"][K]
  ) => {
    setDocument((current) => ({
      ...current,
      header: {...current.header, [key]: value},
    }));
  };

  const updateSeo = <K extends keyof PublicProfileDocument["seo"]>(
    key: K,
    value: PublicProfileDocument["seo"][K]
  ) => {
    setDocument((current) => ({...current, seo: {...current.seo, [key]: value}}));
  };

  const updateThemeOverride = <
    K extends keyof PublicProfileDocument["theme"]["overrides"]
  >(
    key: K,
    value: PublicProfileDocument["theme"]["overrides"][K]
  ) => {
    setDocument((current) => ({
      ...current,
      theme: {
        ...current.theme,
        overrides: {...current.theme.overrides, [key]: value},
      },
    }));
  };

  const updateThemeSetting = <K extends keyof PublicProfileThemeSettings>(
    key: K,
    value: PublicProfileThemeSettings[K]
  ) => {
    setDocument((current) => ({
      ...current,
      theme: {
        ...current.theme,
        settings: {
          ...resolvePublicProfileThemeSettings(current.theme),
          [key]: value,
        },
      },
    }));
  };

  const updateBlock = (nextBlock: PublicProfileBlock) => {
    setDocument((current) => ({
      ...current,
      blocks: current.blocks.map((block) =>
        block.id === nextBlock.id ? nextBlock : block
      ),
    }));
  };

  const moveBlock = (index: number, direction: -1 | 1) => {
    const nextIndex = index + direction;
    if (nextIndex < 0 || nextIndex >= document.blocks.length) return;
    setDocument((current) => {
      const blocks = [...current.blocks];
      [blocks[index], blocks[nextIndex]] = [blocks[nextIndex], blocks[index]];
      return {...current, blocks};
    });
  };

  const reorderBlocks = (orderedBlockIds: string[]) => {
    setDocument((current) => ({
      ...current,
      blocks: reorderPublicProfileBlocks(current.blocks, orderedBlockIds),
    }));
  };

  const addBlock = (selection: PublicProfileBlockLibraryItem) => {
    let block = createPublicProfileBlock(selection.type, {
      imageUrl:
        profileState.profileData.cover_image ?? profileState.profileData.avatar ?? null,
    });
    if (block.type === "offeringCollection" && selection.offeringCollection) {
      block = {
        ...block,
        title: selection.name,
        collection: selection.offeringCollection,
        source: {mode: "selected", offerings: []},
      };
    }
    setDocument((current) => ({...current, blocks: [...current.blocks, block]}));
    setExpandedBlockId(block.id);
    setAddBlockVisible(false);
  };

  const deleteBlock = (id: string) => {
    setDocument((current) => ({
      ...current,
      blocks: current.blocks.filter((block) => block.id !== id),
    }));
    setExpandedBlockId((current) => (current === id ? null : current));
  };

  const handleSave = async () => {
    if (validationIssues.length) {
      setShowValidationIssues(true);
      message.error("Fix the page issues before saving the draft");
      return;
    }
    if (visualQaState) {
      setBaseline(serializedDocument);
      message.success("Fixture changes saved locally");
      return;
    }
    const previousDocument = baseline
      ? (JSON.parse(baseline) as PublicProfileDocument)
      : document;
    try {
      const contentDocument = await saveContentBlocks.mutateAsync({
        document,
        previousDocument,
        onBlockSaved: (localId, persistedId) => {
          setDocument((current) => ({
            ...current,
            blocks: current.blocks.map((block) =>
              block.id === localId ? {...block, id: persistedId} : block
            ),
          }));
          setBaseline((current) => {
            const baselineDocument = current
              ? (JSON.parse(current) as PublicProfileDocument)
              : createDefaultPublicProfileDocument();
            const savedBlock = document.blocks.find((block) => block.id === localId);
            if (!savedBlock) return current;
            const persistedBlock = {...savedBlock, id: persistedId};
            const existingIndex = baselineDocument.blocks.findIndex(
              (block) => block.id === localId || block.id === persistedId
            );
            const blocks = [...baselineDocument.blocks];
            if (existingIndex >= 0) blocks[existingIndex] = persistedBlock;
            else blocks.push(persistedBlock);
            return JSON.stringify({...baselineDocument, blocks});
          });
        },
        onBlockDeleted: (persistedId) =>
          setBaseline((current) => {
            if (!current) return current;
            const baselineDocument = JSON.parse(current) as PublicProfileDocument;
            return JSON.stringify({
              ...baselineDocument,
              blocks: baselineDocument.blocks.filter((block) => block.id !== persistedId),
            });
          }),
      });
      const theme = await saveTheme.mutateAsync(contentDocument.theme);
      const themedDocument = {...contentDocument, theme};
      setDocument(themedDocument);
      setBaseline(JSON.stringify(themedDocument));
      setShowValidationIssues(false);
      message.success("Content and theme saved");
    } catch {
      // Mutation hooks display the actionable API error and retain reconciled progress.
    }
  };

  const handleShare = async () => {
    try {
      await navigator.clipboard.writeText(publicUrl);
      message.success("Public profile link copied");
    } catch {
      message.info(publicUrl);
    }
  };

  const canonicalUrl = document.seo.canonicalUrl || publicUrl;
  const seoPreviewDescription =
    document.seo.description ||
    profileState.profileData.shortBio ||
    profileState.profileData.description ||
    "";
  const seoPreviewTitle =
    document.seo.title ||
    [profileDisplayName, ...(document.header.professionalTitles ?? [])]
      .filter(Boolean)
      .join(" - ");
  const seoPreviewImageUrl =
    document.seo.openGraphImageUrl ||
    document.header.coverImage?.url ||
    profileState.profileData.cover_image ||
    profileImageUrl;
  const handleCopyCanonicalUrl = async () => {
    try {
      await navigator.clipboard.writeText(canonicalUrl);
      message.success("Canonical URL copied");
    } catch {
      message.info(canonicalUrl);
    }
  };
  const handleSeoPreviewImageUpload = (file: File) => {
    uploadThemeImage.mutate(file, {
      onSuccess: ({url}) => updateSeo("openGraphImageUrl", url),
    });
    return false;
  };

  if (contentBlocksUnavailable || themeUnavailable) {
    const failedRequests = [
      contentBlocksUnavailable
        ? `Content blocks: ${getQueryErrorMessage(contentBlocksQuery.error)}`
        : null,
      themeUnavailable ? `Theme: ${getQueryErrorMessage(themeQuery.error)}` : null,
    ].filter((item): item is string => Boolean(item));
    return renderPage(
      <Alert
        type="error"
        showIcon
        message="The public profile builder is unavailable"
        description={failedRequests.join(" ")}
        action={
          <Button
            onClick={() => {
              contentBlocksQuery.refetch();
              themeQuery.refetch();
            }}
          >
            Try again
          </Button>
        }
      />
    );
  }

  if (
    (!visualQaState && contentBlocksQuery.isLoading) ||
    (!visualQaState && themeQuery.isLoading) ||
    !initialised
  ) {
    return renderPage(
      <div style={{display: "grid", minHeight: "40rem", placeItems: "center"}}>
        <Spin size="large" />
      </div>
    );
  }

  const contentTab = (
    <>
      <ContentSectionCard $expanded={headerExpanded}>
        <button
          type="button"
          className="section-toggle"
          aria-expanded={headerExpanded}
          onClick={() => setHeaderExpanded((current) => !current)}
        >
          <span>Header</span>
          <span
            className={`content-accordion-chevron ${headerExpanded ? "open" : ""}`}
            aria-hidden="true"
          >
            ⌄
          </span>
        </button>
        {headerExpanded ? (
          <div className="section-body">
            <FieldGroup>
              <label>Layout</label>
              <HeaderLayoutGrid>
                {(
                  [
                    ["basic-left", "Basic left"],
                    ["basic-center", "Basic centre"],
                    ["full", "Full"],
                  ] as const
                ).map(([value, label]) => (
                  <HeaderLayoutOption
                    key={value}
                    type="button"
                    $selected={document.header.layout === value}
                    aria-pressed={document.header.layout === value}
                    onClick={() => updateHeader("layout", value)}
                  >
                    <span className={`layout-preview ${value}`} aria-hidden="true">
                      <i className="cover" />
                      <i className="avatar" />
                      <i className="line line-one" />
                      <i className="line line-two" />
                    </span>
                    <span>{label}</span>
                  </HeaderLayoutOption>
                ))}
              </HeaderLayoutGrid>
            </FieldGroup>

            <HeaderProfileFields>
              <div className="image-setting profile-image-setting">
                <img src={profileImageUrl} alt="" />
                <div>
                  <strong>Upload profile image</strong>
                  <span>Min 400×400px, PNG or JPEG</span>
                  <div className="image-actions">
                    {profileState.profileData.avatar && document.header.showAvatar ? (
                      <button
                        type="button"
                        className="image-action remove-image-action"
                        onClick={() => updateHeader("showAvatar", false)}
                      >
                        Remove
                      </button>
                    ) : null}
                    <a className="image-action change-image-action" href="/profile">
                      {profileState.profileData.avatar && document.header.showAvatar
                        ? "Change"
                        : "Upload"}
                    </a>
                  </div>
                </div>
              </div>

              <div className="image-setting cover-image-setting">
                <div className="cover-thumbnail">
                  {document.header.coverImage?.url ||
                  profileState.profileData.cover_image ? (
                    <img
                      src={
                        document.header.coverImage?.url ??
                        profileState.profileData.cover_image ??
                        ""
                      }
                      alt=""
                    />
                  ) : null}
                </div>
                <div>
                  <strong>Upload cover image</strong>
                  <span>Min 640×200px, PNG or JPEG</span>
                  <div className="image-actions">
                    {(document.header.coverImage?.url ||
                      profileState.profileData.cover_image) &&
                    document.header.showCoverImage ? (
                      <button
                        type="button"
                        className="image-action remove-image-action"
                        onClick={() => {
                          updateHeader("coverImage", null);
                          updateHeader("showCoverImage", false);
                        }}
                      >
                        Remove
                      </button>
                    ) : null}
                    <a className="image-action change-image-action" href="/profile">
                      {(document.header.coverImage?.url ||
                        profileState.profileData.cover_image) &&
                      document.header.showCoverImage
                        ? "Change"
                        : "Upload"}
                    </a>
                    <button
                      type="button"
                      className="image-action icon-image-action"
                      aria-label="Choose a cover image from Unsplash"
                      title="Choose from Unsplash"
                      onClick={() => setUnsplashPickerTarget("cover")}
                    >
                      <img src="/icons/unsplash-fill.png" alt="" aria-hidden="true" />
                    </button>
                  </div>
                </div>
              </div>

              <SettingRow className="header-setting-row">
                <span className="label">Hide cover image</span>
                <Switch
                  size="small"
                  checked={!document.header.showCoverImage}
                  aria-label="Hide cover image"
                  onChange={(checked) => updateHeader("showCoverImage", !checked)}
                />
              </SettingRow>

              <FieldGroup className="header-field-group">
                <label>Public display name</label>
                <Input value={profileState.profileData.display_name ?? ""} readOnly />
                <small>
                  Your public display name must be yours&nbsp; • 1 change every 14 days
                </small>
              </FieldGroup>
              <FieldGroup className="header-field-group">
                <label>Username</label>
                <Input addonBefore="https://coachli.co/" value={username} readOnly />
              </FieldGroup>
              <FieldGroup className="header-field-group">
                <label>First name</label>
                <Input value={profileState.profileData.firstName} readOnly />
              </FieldGroup>
              <FieldGroup className="header-field-group">
                <label>Last name</label>
                <Input value={profileState.profileData.lastName} readOnly />
              </FieldGroup>
              <FieldGroup className="header-field-group">
                <label>Country</label>
                <Select
                  value={profileState.profileData.country || undefined}
                  placeholder="Select your country"
                  open={false}
                  style={{width: "100%"}}
                />
              </FieldGroup>
              <FieldGroup className="header-field-group">
                <label>City</label>
                <Select
                  placeholder="Select your city"
                  open={false}
                  style={{width: "100%"}}
                />
              </FieldGroup>
              <SettingRow className="header-setting-row">
                <span className="label">Hide location</span>
                <Switch
                  size="small"
                  checked={!document.header.showLocation}
                  aria-label="Hide location"
                  onChange={(checked) => updateHeader("showLocation", !checked)}
                />
              </SettingRow>
              <FieldGroup className="header-field-group">
                <label>Short bio</label>
                <Input.TextArea
                  value={profileState.profileData.shortBio}
                  readOnly
                  autoSize={{minRows: 3, maxRows: 5}}
                  showCount
                  maxLength={115}
                />
              </FieldGroup>
              <FieldGroup className="header-field-group">
                <label>Professional title</label>
                <Select
                  mode="multiple"
                  showSearch
                  maxTagCount={3}
                  value={document.header.professionalTitles ?? []}
                  placeholder="Select title"
                  style={{width: "100%"}}
                  options={[
                    "Blogger",
                    "Brand designer",
                    "Business coach",
                    "Business owner",
                    "Career coach",
                    "Consultant",
                    "Content creator",
                    "Mentor",
                    "Product designer",
                  ].map((title) => ({value: title, label: title}))}
                  onChange={(titles: string[]) =>
                    updateHeader("professionalTitles", titles.slice(0, 3))
                  }
                />
                <small>Add up to 3 titles</small>
              </FieldGroup>
              <FieldGroup className="header-field-group">
                <label>Years of experience</label>
                <Input
                  value={document.header.yearsExperience ?? ""}
                  placeholder="e.g., 5"
                  onChange={(event) =>
                    updateHeader("yearsExperience", event.target.value)
                  }
                />
              </FieldGroup>

              <FieldGroup className="header-field-group">
                <label>Social links</label>
                {editableSocialLinks.map((social, index) => (
                  <Input
                    key={`${index}-${social.name}`}
                    addonBefore="URL"
                    value={social.url}
                    placeholder="https://"
                    onChange={(event) =>
                      updateSocialLink(index, "url", event.target.value)
                    }
                    addonAfter={
                      <Select
                        bordered={false}
                        value={social.name}
                        aria-label={`Platform for social link ${index + 1}`}
                        onChange={(value) => updateSocialLink(index, "name", value)}
                        options={SOCIAL_PLATFORM_OPTIONS}
                      />
                    }
                  />
                ))}
                <small className="social-links-hint">Add up to 5 links</small>
                <Button
                  block
                  className="add-social-link-action"
                  disabled={editableSocialLinks.length >= 5}
                  onClick={addSocialLink}
                >
                  <PlusOutlined /> Add link
                </Button>
              </FieldGroup>
            </HeaderProfileFields>
          </div>
        ) : null}
      </ContentSectionCard>

      {document.blocks.length > 0 ||
      (showValidationIssues && validationIssues.length > 0) ? (
        <div className="page-blocks" aria-label="Page blocks">
          {showValidationIssues && validationIssues.length ? (
            <ValidationSummary>
              <Alert
                type="error"
                showIcon
                message={`${validationIssues.length} page issue${
                  validationIssues.length === 1 ? "" : "s"
                } must be fixed before saving`}
                description={
                  validationIssues
                    .filter((issue) => !issue.blockId)
                    .slice(0, 3)
                    .map((issue) => issue.message)
                    .join(" ") || "Open the highlighted blocks to review their fields."
                }
              />
            </ValidationSummary>
          ) : null}
          <Reorder.Group
            as="div"
            axis="y"
            values={document.blocks.map((block) => block.id)}
            onReorder={reorderBlocks}
            aria-label="Reorder page blocks"
          >
            {document.blocks.map((block, index) => (
              <PublicProfileBlockEditor
                key={block.id}
                block={block}
                index={index}
                total={document.blocks.length}
                expanded={expandedBlockId === block.id}
                issues={
                  showValidationIssues
                    ? validationIssues
                        .filter((issue) => issue.blockId === block.id)
                        .map((issue) => issue.message)
                    : []
                }
                onToggle={() =>
                  setExpandedBlockId((current) =>
                    current === block.id ? null : block.id
                  )
                }
                onChange={updateBlock}
                onMove={(direction) => moveBlock(index, direction)}
                onDelete={() => deleteBlock(block.id)}
              />
            ))}
          </Reorder.Group>
        </div>
      ) : null}

      <ContentSectionCard $expanded={false}>
        <div className="section-toggle footer-static-label">
          <span>Footer</span>
        </div>
      </ContentSectionCard>

      <ContentActions $showSave={showSaveAction}>
        {showSaveAction ? (
          <Button
            className="content-save-action"
            icon={<img src="/icons/save-fill.png" alt="" aria-hidden="true" />}
            disabled={!isDirty}
            loading={saveContentBlocks.isLoading || saveTheme.isLoading}
            onClick={handleSave}
          >
            Save
          </Button>
        ) : null}
        <Button
          type="primary"
          icon={<PlusOutlined />}
          disabled={document.blocks.length >= 50}
          onClick={() => setAddBlockVisible(true)}
        >
          Add a block
        </Button>
      </ContentActions>
    </>
  );

  const themeSettings = resolvePublicProfileThemeSettings(document.theme);
  const themeFallbacks = PRESET_COLOUR_FALLBACKS[document.theme.preset];
  const surfaceStyleOptions: Array<{value: PublicProfileSurfaceStyle; label: string}> = [
    {value: "filled", label: "Filled"},
    {value: "outlined", label: "Outlined"},
    {value: "hard-shadow", label: "Hard shadow"},
    {value: "glass", label: "Glass"},
  ];

  const designTab = (
    <>
      <ThemeAccordion open>
        <summary>Presets</summary>
        <div className="theme-accordion-body">
          <ThemeGrid>
            {PUBLIC_PROFILE_THEMES.filter((theme) =>
              VISIBLE_THEME_PRESETS.includes(theme.id)
            ).map((theme) => (
              <ThemeCard
                type="button"
                key={theme.id}
                $selected={document.theme.preset === theme.id}
                aria-pressed={document.theme.preset === theme.id}
                aria-label={`${theme.name}: ${theme.description}`}
                onClick={() =>
                  setDocument((current) => ({
                    ...current,
                    theme: {
                      preset: theme.id,
                      settings: {...theme.settings},
                      overrides: {
                        pageBackground: null,
                        surface: null,
                        surfaceMuted: null,
                        text: null,
                        textMuted: null,
                        accent: null,
                        accentText: null,
                      },
                    },
                  }))
                }
              >
                <span className="theme-name">
                  <i className="radio-indicator" />
                  <strong>{theme.name}</strong>
                </span>
                <i
                  className="theme-orb"
                  style={{background: THEME_PRESET_PREVIEWS[theme.id]}}
                />
              </ThemeCard>
            ))}
          </ThemeGrid>
          <h3>Background</h3>
          <BackgroundStyleGrid>
            {[
              {value: "solid", label: "Solid colour", icon: "/icons/paint-fill.png"},
              {
                value: "gradient",
                label: "Gradient",
                icon: "/icons/contrast-drop-2-fill.png",
              },
              {value: "image", label: "Image", icon: "/icons/image-fill.png"},
            ].map(({value, label, icon}) => (
              <BackgroundStyleCard
                type="button"
                key={value}
                $selected={themeSettings.backgroundStyle === value}
                aria-pressed={themeSettings.backgroundStyle === value}
                onClick={() =>
                  updateThemeSetting(
                    "backgroundStyle",
                    value as PublicProfileThemeSettings["backgroundStyle"]
                  )
                }
              >
                <span className="background-style-label">
                  <i className="radio-indicator" aria-hidden="true" />
                  {label}
                </span>
                <img
                  className="background-style-icon"
                  src={icon}
                  alt=""
                  aria-hidden="true"
                />
              </BackgroundStyleCard>
            ))}
          </BackgroundStyleGrid>
          <ColourOverrideGrid>
            <ThemeColourInput
              label={themeSettings.backgroundStyle === "gradient" ? "Color 1" : "Color"}
              value={document.theme.overrides.pageBackground}
              fallback={themeFallbacks.pageBackground}
              allowPreset
              onChange={(value) => updateThemeOverride("pageBackground", value)}
            />
            {themeSettings.backgroundStyle === "gradient" ? (
              <ThemeColourInput
                label="Color 2"
                value={themeSettings.backgroundGradientEnd}
                fallback={themeFallbacks.pageBackground}
                onChange={(value) =>
                  updateThemeSetting(
                    "backgroundGradientEnd",
                    value ?? themeFallbacks.pageBackground
                  )
                }
              />
            ) : null}
          </ColourOverrideGrid>
          {themeSettings.backgroundStyle === "image" ? (
            <FieldGroup>
              <label>Background image</label>
              <div className="inline-action-field">
                <Input
                  type="url"
                  value={themeSettings.backgroundImageUrl ?? ""}
                  placeholder="https://…"
                  onChange={(event) =>
                    updateThemeSetting("backgroundImageUrl", event.target.value || null)
                  }
                />
                <Upload
                  accept="image/jpeg,image/png,image/gif,image/webp"
                  showUploadList={false}
                  beforeUpload={(file) => {
                    uploadThemeImage.mutate(file, {
                      onSuccess: ({url}) => updateThemeSetting("backgroundImageUrl", url),
                    });
                    return false;
                  }}
                >
                  <Button icon={<UploadOutlined />} loading={uploadThemeImage.isLoading}>
                    Upload
                  </Button>
                </Upload>
              </div>
            </FieldGroup>
          ) : null}
        </div>
      </ThemeAccordion>

      <ThemeAccordion>
        <summary>Typography</summary>
        <div className="theme-accordion-body">
          <FieldGroup>
            <label>Header font</label>
            <Select
              showSearch
              value={themeSettings.headingFont}
              options={PUBLIC_PROFILE_THEME_FONTS.map((font) => ({
                value: font,
                label: font,
              }))}
              style={{width: "100%"}}
              onChange={(font) => updateThemeSetting("headingFont", font)}
            />
          </FieldGroup>
          <FieldGroup>
            <label>Body font</label>
            <Select
              showSearch
              value={themeSettings.bodyFont}
              options={PUBLIC_PROFILE_THEME_FONTS.map((font) => ({
                value: font,
                label: font,
              }))}
              style={{width: "100%"}}
              onChange={(font) => updateThemeSetting("bodyFont", font)}
            />
          </FieldGroup>
          <ColourOverrideGrid>
            <ThemeColourInput
              label="Color"
              value={document.theme.overrides.text}
              fallback={themeFallbacks.text}
              allowPreset
              onChange={(value) => updateThemeOverride("text", value)}
            />
          </ColourOverrideGrid>
        </div>
      </ThemeAccordion>

      <ThemeAccordion>
        <summary>Buttons</summary>
        <div className="theme-accordion-body">
          <FieldGroup>
            <label>Style</label>
            <ButtonStyleGrid>
              {surfaceStyleOptions.map(({value, label}) => (
                <ButtonStyleCard
                  type="button"
                  key={value}
                  $selected={themeSettings.buttonStyle === value}
                  aria-pressed={themeSettings.buttonStyle === value}
                  onClick={() => updateThemeSetting("buttonStyle", value)}
                >
                  <span className="button-style-label">
                    <i aria-hidden="true" />
                    {label}
                  </span>
                  <span className={`button-style-preview ${value}`}>Button</span>
                </ButtonStyleCard>
              ))}
            </ButtonStyleGrid>
          </FieldGroup>
          <ColourOverrideGrid>
            <ThemeColourInput
              label="Button colour"
              value={document.theme.overrides.accent}
              fallback={themeFallbacks.accent}
              allowPreset
              onChange={(value) => updateThemeOverride("accent", value)}
            />
            <ThemeColourInput
              label="Text colour"
              value={document.theme.overrides.accentText}
              fallback={themeFallbacks.accentText}
              allowPreset
              onChange={(value) => updateThemeOverride("accentText", value)}
            />
          </ColourOverrideGrid>
          <FieldGroup className="button-radius-field">
            <label>Border radius</label>
            <div className="button-radius-control">
              <Slider
                min={0}
                max={32}
                value={themeSettings.buttonRadius}
                onChange={(value) => updateThemeSetting("buttonRadius", value)}
              />
              <InputNumber
                min={0}
                max={32}
                precision={0}
                value={themeSettings.buttonRadius}
                onChange={(value) =>
                  updateThemeSetting("buttonRadius", Number(value ?? 0))
                }
              />
              <span className="radius-unit">px</span>
            </div>
          </FieldGroup>
        </div>
      </ThemeAccordion>

      <ThemeAccordion>
        <summary>Blocks</summary>
        <div className="theme-accordion-body">
          <FieldGroup>
            <label>Style</label>
            <BlockStyleGrid>
              {surfaceStyleOptions.map(({value, label}) => (
                <BlockStyleCard
                  type="button"
                  key={value}
                  $selected={themeSettings.blockStyle === value}
                  aria-pressed={themeSettings.blockStyle === value}
                  onClick={() => updateThemeSetting("blockStyle", value)}
                >
                  <span className="button-style-label">
                    <i aria-hidden="true" />
                    {label}
                  </span>
                  <span
                    className={`button-style-preview block-style-preview ${value}`}
                    aria-hidden="true"
                  />
                </BlockStyleCard>
              ))}
            </BlockStyleGrid>
          </FieldGroup>
          <ColourOverrideGrid>
            <ThemeColourInput
              label="Colour"
              value={document.theme.overrides.surfaceMuted}
              fallback={themeFallbacks.surfaceMuted}
              allowPreset
              onChange={(value) => updateThemeOverride("surfaceMuted", value)}
            />
          </ColourOverrideGrid>
          <FieldGroup className="button-radius-field">
            <label>Border radius</label>
            <div className="button-radius-control">
              <Slider
                min={0}
                max={32}
                value={themeSettings.blockRadius}
                onChange={(value) => updateThemeSetting("blockRadius", value)}
              />
              <InputNumber
                min={0}
                max={32}
                precision={0}
                value={themeSettings.blockRadius}
                onChange={(value) =>
                  updateThemeSetting("blockRadius", Number(value ?? 0))
                }
              />
              <span className="radius-unit">px</span>
            </div>
          </FieldGroup>
          <FieldGroup className="button-radius-field">
            <label>Spacing</label>
            <div className="button-radius-control">
              <Slider
                min={0}
                max={32}
                value={themeSettings.blockSpacing}
                onChange={(value) => updateThemeSetting("blockSpacing", value)}
              />
              <InputNumber
                min={0}
                max={32}
                precision={0}
                value={themeSettings.blockSpacing}
                onChange={(value) =>
                  updateThemeSetting("blockSpacing", Number(value ?? 0))
                }
              />
              <span className="radius-unit">px</span>
            </div>
          </FieldGroup>
        </div>
      </ThemeAccordion>

      <ThemeAccordion>
        <summary>Highlights</summary>
        <div className="theme-accordion-body">
          <ColourOverrideGrid>
            <ThemeColourInput
              label="Background colour"
              value={themeSettings.highlightBackground}
              fallback={themeSettings.highlightBackground}
              onChange={(value) =>
                updateThemeSetting(
                  "highlightBackground",
                  value ?? themeSettings.highlightBackground
                )
              }
            />
            <ThemeColourInput
              label="Border colour"
              value={themeSettings.highlightBorderColor}
              fallback={themeSettings.highlightBorderColor}
              onChange={(value) =>
                updateThemeSetting(
                  "highlightBorderColor",
                  value ?? themeSettings.highlightBorderColor
                )
              }
            />
          </ColourOverrideGrid>
          <FieldGroup className="button-radius-field">
            <label>Border radius</label>
            <div className="button-radius-control">
              <Slider
                min={0}
                max={32}
                value={themeSettings.highlightRadius}
                onChange={(value) => updateThemeSetting("highlightRadius", value)}
              />
              <InputNumber
                min={0}
                max={32}
                precision={0}
                value={themeSettings.highlightRadius}
                onChange={(value) =>
                  updateThemeSetting("highlightRadius", Number(value ?? 0))
                }
              />
              <span className="radius-unit">px</span>
            </div>
          </FieldGroup>
        </div>
      </ThemeAccordion>

      <ThemeAccordion>
        <summary>Surfaces</summary>
        <div className="theme-accordion-body">
          <ColourOverrideGrid>
            <ThemeColourInput
              label="Background colour"
              value={document.theme.overrides.surface}
              fallback={themeFallbacks.surface}
              allowPreset
              onChange={(value) => updateThemeOverride("surface", value)}
            />
            <ThemeColourInput
              label="Border colour"
              value={themeSettings.surfaceBorderColor}
              fallback={themeSettings.surfaceBorderColor}
              onChange={(value) =>
                updateThemeSetting(
                  "surfaceBorderColor",
                  value ?? themeSettings.surfaceBorderColor
                )
              }
            />
          </ColourOverrideGrid>
          <FieldGroup className="button-radius-field">
            <label>Border radius</label>
            <div className="button-radius-control">
              <Slider
                min={0}
                max={32}
                value={themeSettings.surfaceRadius}
                onChange={(value) => updateThemeSetting("surfaceRadius", value)}
              />
              <InputNumber
                min={0}
                max={32}
                precision={0}
                value={themeSettings.surfaceRadius}
                onChange={(value) =>
                  updateThemeSetting("surfaceRadius", Number(value ?? 0))
                }
              />
              <span className="radius-unit">px</span>
            </div>
          </FieldGroup>
        </div>
      </ThemeAccordion>
    </>
  );

  const seoTab = (
    <SeoSettingsCard>
      <div className="seo-description-field">
        <label className="seo-field-label" htmlFor="public-profile-meta-description">
          Meta description
          <QuestionCircleOutlined
            className="seo-help-icon"
            title="A short summary shown in search and social previews."
          />
        </label>
        <Input.TextArea
          id="public-profile-meta-description"
          rows={4}
          maxLength={160}
          value={document.seo.description ?? ""}
          placeholder="Describe your public profile"
          onChange={(event) => updateSeo("description", event.target.value || null)}
        />
        <div className="count">{document.seo.description?.length ?? 0}/160</div>
      </div>

      <div className="seo-preview-header">
        <div className="seo-preview-heading">
          Preview
          <QuestionCircleOutlined
            className="seo-help-icon"
            title="A preview of how your profile may appear when shared."
          />
        </div>
        <div className="seo-platforms" aria-label="Sharing platforms">
          <span className="seo-platform" title="Google">
            <FcGoogle aria-hidden="true" />
          </span>
          <span className="seo-platform x" title="X">
            <FaXTwitter aria-hidden="true" />
          </span>
          <span className="seo-platform whatsapp" title="WhatsApp">
            <FaWhatsapp aria-hidden="true" />
          </span>
          <span className="seo-platform linkedin" title="LinkedIn">
            <FaLinkedinIn aria-hidden="true" />
          </span>
          <span className="seo-platform facebook" title="Facebook">
            <FaFacebookF aria-hidden="true" />
          </span>
          <span className="seo-platform slack" title="Slack">
            <FaSlack aria-hidden="true" />
          </span>
        </div>
      </div>

      <div className="seo-preview-card">
        <p className="seo-preview-url">{canonicalUrl}</p>
        <p className="seo-preview-title">{seoPreviewTitle}</p>
        <p className="seo-preview-description">{seoPreviewDescription}</p>
      </div>

      <div className="seo-image-setting">
        <div className="seo-image-preview">
          <img src={seoPreviewImageUrl} alt="Current sharing preview" />
        </div>
        <div className="seo-image-copy">
          <strong>Update preview image</strong>
          <span>1200×630px, PNG or JPEG</span>
          <div className="seo-image-actions">
            <Upload
              accept="image/jpeg,image/png"
              showUploadList={false}
              beforeUpload={handleSeoPreviewImageUpload}
            >
              <Button loading={uploadThemeImage.isLoading}>Upload</Button>
            </Upload>
            <Button
              className="seo-unsplash-button"
              aria-label="Choose a preview image from Unsplash"
              title="Choose from Unsplash"
              onClick={() => setUnsplashPickerTarget("seo")}
            >
              <img src="/icons/unsplash-fill.png" alt="" aria-hidden="true" />
            </Button>
          </div>
        </div>
      </div>

      <FieldGroup className="canonical-field">
        <label>Canonical URL</label>
        <Input
          type="url"
          value={canonicalUrl}
          onChange={(event) => updateSeo("canonicalUrl", event.target.value || null)}
          addonAfter={
            <Button
              className="canonical-copy-button"
              type="text"
              icon={<CopyOutlined />}
              aria-label="Copy canonical URL"
              onClick={handleCopyCanonicalUrl}
            />
          }
        />
      </FieldGroup>
    </SeoSettingsCard>
  );

  return renderPage(
    <BuilderPage>
      <BuilderHeader>
        <h1>Public profile</h1>
      </BuilderHeader>

      <BuilderCommandBar>
        <div className="device-switch" aria-label="Preview device">
          <Button
            type={!mobilePreview ? "default" : "text"}
            icon={<DesktopOutlined />}
            aria-label="Desktop preview"
            onClick={() => setMobilePreview(false)}
          />
          <Button
            type={mobilePreview ? "default" : "text"}
            icon={<MobileOutlined />}
            aria-label="Mobile preview"
            onClick={() => setMobilePreview(true)}
          />
        </div>
        <div className="command-actions">
          <Dropdown
            trigger={["click"]}
            placement="bottomRight"
            menu={{
              items: [
                {
                  key: "preview",
                  label: (
                    <a href={publicUrl} target="_blank" rel="noreferrer">
                      Preview profile
                    </a>
                  ),
                },
                {
                  key: "share",
                  label: "Copy profile link",
                  onClick: handleShare,
                },
              ],
            }}
          >
            <Button
              className="mobile-more-button"
              type="default"
              icon={<MoreOutlined />}
              aria-label="More profile actions"
            />
          </Dropdown>
          <Button href={publicUrl} target="_blank">
            Preview
            <img
              className="command-button-icon preview-icon"
              src="/icons/public-profile-preview.svg"
              alt=""
              aria-hidden="true"
            />
          </Button>
          <Button className="public-url-button" onClick={handleShare}>
            <span>{publicUrlLabel}</span>
            <img
              className="command-button-icon share-icon"
              src="/icons/public-profile-share.svg"
              alt=""
              aria-hidden="true"
            />
          </Button>
          <Button
            type="primary"
            loading={saveContentBlocks.isLoading || saveTheme.isLoading}
            onClick={handleSave}
          >
            Publish
            <img
              className="command-button-icon publish-icon"
              src="/icons/public-profile-publish.svg"
              alt=""
              aria-hidden="true"
            />
          </Button>
        </div>
      </BuilderCommandBar>

      <BuilderShell>
        <EditorPanel $trayExpanded={mobileTrayExpanded}>
          <button
            className="mobile-tray-handle"
            type="button"
            aria-label={
              mobileTrayExpanded ? "Collapse profile editor" : "Expand profile editor"
            }
            aria-expanded={mobileTrayExpanded}
            onPointerDown={handleMobileTrayPointerDown}
            onPointerUp={handleMobileTrayPointerUp}
            onPointerCancel={() => {
              mobileTrayDragStart.current = null;
            }}
            onKeyDown={(event) => {
              if (event.key !== "Enter" && event.key !== " ") return;
              event.preventDefault();
              setMobileTrayExpanded((current) => !current);
            }}
          >
            <span />
          </button>
          <Tabs
            activeKey={activeTab}
            onChange={(key) => setActiveTab(key as PublicProfileBuilderTab)}
            items={[
              {
                key: "content",
                label: (
                  <span className="tab-label">
                    <span
                      className="public-profile-tab-icon content-icon"
                      aria-hidden="true"
                    />
                    Content
                  </span>
                ),
                children: contentTab,
              },
              {
                key: "design",
                label: (
                  <span className="tab-label">
                    <span
                      className="public-profile-tab-icon theme-icon"
                      aria-hidden="true"
                    />
                    Theme
                  </span>
                ),
                children: designTab,
              },
              {
                key: "seo",
                label: (
                  <span className="tab-label seo-tab-label" aria-label="SEO and sharing">
                    <span
                      className="public-profile-tab-icon seo-icon"
                      aria-hidden="true"
                    />
                    <span className="seo-label-long" aria-hidden="true">
                      SEO and sharing
                    </span>
                    <span className="seo-label-short" aria-hidden="true">
                      SEO
                    </span>
                  </span>
                ),
                children: seoTab,
              },
            ]}
          />
        </EditorPanel>

        <PreviewPanel>
          <Button
            className="mobile-edit-profile-button"
            type="primary"
            shape="circle"
            icon={<EditOutlined />}
            aria-label="Edit public profile"
            onClick={() => setMobileTrayExpanded(true)}
          />
          <PreviewViewport $mobile={mobilePreview}>
            <LocalPublicProfilePreview
              document={document}
              mobilePreview={mobilePreview}
              displayName={profileDisplayName}
              username={username}
              profileImageUrl={profileImageUrl}
              coverImageUrl={
                document.header.coverImage?.url ?? profileState.profileData.cover_image
              }
              coverImageAttribution={document.header.coverImage ?? null}
              bio={
                profileState.profileData.shortBio || profileState.profileData.description
              }
              location={profileState.profileData.country}
              professionalTitles={document.header.professionalTitles ?? []}
              yearsExperience={document.header.yearsExperience ?? ""}
              socialLinks={editableSocialLinks.filter((link) => link.url.trim())}
            />
          </PreviewViewport>
        </PreviewPanel>
      </BuilderShell>
      <AddPublicProfileBlockModal
        visible={addBlockVisible}
        onClose={() => setAddBlockVisible(false)}
        onSelect={addBlock}
      />
      <UnsplashPicker
        visible={Boolean(unsplashPickerTarget)}
        onClose={() => setUnsplashPickerTarget(null)}
        onSelect={(image: UnsplashCoverImage) => {
          if (unsplashPickerTarget === "seo") {
            updateSeo("openGraphImageUrl", image.url);
          } else {
            updateHeader("coverImage", image);
            updateHeader("showCoverImage", true);
          }
          setUnsplashPickerTarget(null);
        }}
      />
    </BuilderPage>
  );
}
